import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton, 
  Typography, 
  Divider,
  Badge
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useTheme } from '../../utils/ThemeContext';

const ProfileMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    profilePicture: null
  });
  const [profileComplete, setProfileComplete] = useState(false);
  const { isDarkMode } = useTheme();

  const open = Boolean(anchorEl);

  // Load user data from localStorage
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userProfileData = localStorage.getItem('userProfileData');
        
        if (userProfileData) {
          const profile = JSON.parse(userProfileData);
          console.log('🔍 ProfileMenu: Loaded profile data:', profile);
          setProfileComplete(Boolean(profile.profileComplete));
          
          // Set user data from the profile data
          setUserData(prev => ({
            ...prev,
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '', // Email might not be in profile data
            profilePicture: profile.profilePicture || null
          }));
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();

    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      if (event.detail && event.detail.profileData) {
        const { profileData } = event.detail;
        setProfileComplete(Boolean(profileData.profileComplete));
        setUserData(prev => ({
          ...prev,
          firstName: profileData.firstName || prev.firstName,
          lastName: profileData.lastName || prev.lastName,
          email: profileData.email || prev.email,
          profilePicture: profileData.profilePicture || prev.profilePicture
        }));
      }
    };

    const handleStorageChange = () => {
      loadUserData();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userProfileData');
    localStorage.removeItem('userData');
    window.location.href = '/signin';
  };

  // Generate initials from first and last name
  const getInitials = () => {
    const firstInitial = userData.firstName?.charAt(0)?.toUpperCase() || 'U';
    const lastInitial = userData.lastName?.charAt(0)?.toUpperCase() || 'S';
    return `${firstInitial}${lastInitial}`;
  };

  // Get user display name
  const getDisplayName = () => {
    if (userData.firstName && userData.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    return userData.email || 'User';
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        data-tutorial="profile-avatar"
        sx={{
          padding: '8px',
          marginLeft: '1rem',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(108, 48, 130, 0.1)',
          }
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            !profileComplete ? (
              <div style={{
                width: '16px',
                height: '16px',
                background: '#ff6b6b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold',
                border: '2px solid #1a237e',
                animation: 'pulse 2s infinite'
              }}>
                !
              </div>
            ) : (
              // Online status indicator
              <div style={{
                width: '12px',
                height: '12px',
                background: 'var(--success)',
                borderRadius: '50%',
                border: `2px solid ${isDarkMode ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)'}`,
                boxShadow: '0 0 0 1px rgba(70, 130, 48, 0.3)'
              }} />
            )
          }
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: userData.profilePicture 
                ? 'transparent' 
                : 'var(--accent-gradient)',
              color: userData.profilePicture ? 'inherit' : '#ffffff',
              fontWeight: 'bold',
              fontSize: '16px',
              border: `2px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
            src={userData.profilePicture}
            alt={getDisplayName()}
          >
            {!userData.profilePicture && getInitials()}
          </Avatar>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            background: isDarkMode 
              ? 'rgba(20, 20, 20, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: isDarkMode 
              ? '1px solid rgba(108, 48, 130, 0.3)' 
              : '1px solid rgba(108, 48, 130, 0.2)',
            borderRadius: '4px',
            boxShadow: isDarkMode 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            mt: 1
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <MenuItem
          sx={{
            cursor: 'default',
            pointerEvents: 'none',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            padding: '8px 0'
          }}>
            <div style={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: userData.profilePicture 
                    ? 'transparent' 
                    : 'var(--accent-gradient)',
                  color: userData.profilePicture ? 'inherit' : '#ffffff',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
                src={userData.profilePicture}
                alt={getDisplayName()}
              >
                {!userData.profilePicture && getInitials()}
              </Avatar>
              {/* Online status indicator */}
              <div style={{
                position: 'absolute',
                bottom: '0px',
                right: '0px',
                width: '10px',
                height: '10px',
                background: 'var(--success)',
                borderRadius: '50%',
                border: `2px solid ${isDarkMode ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)'}`,
                boxShadow: '0 0 0 1px rgba(70, 130, 48, 0.3)'
              }} />
            </div>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'var(--text-primary)',
                  fontWeight: '700',
                  fontSize: '15px',
                  opacity: 1
                }}
              >
                {getDisplayName()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--text-muted)',
                  fontSize: '12px',
                  opacity: 0.8
                }}
              >
                {userData.email}
              </Typography>
            </div>
          </div>
        </MenuItem>

        <Divider sx={{ 
          borderColor: isDarkMode 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.1)',
          my: 0.5
        }} />

        {/* Profile Link */}
        <MenuItem
          component={Link}
          to="/profile"
          onClick={handleClose}
          data-tutorial="profile-menu-item"
          sx={{
            color: 'var(--text-primary)',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            margin: '0.25rem',
            backgroundColor: isDarkMode 
              ? 'rgba(255, 255, 255, 0.05) !important' 
              : 'rgba(0, 0, 0, 0.05) !important',
            border: isDarkMode 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(108, 48, 130, 0.1) !important',
              border: '1px solid #6c3082',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(108, 48, 130, 0.2) !important',
              color: 'var(--neon-cyan)'
            }
          }}
        >
          <PersonIcon sx={{ mr: 2, fontSize: '20px' }} />
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            Profile
          </Typography>
        </MenuItem>

        {/* Logout */}
        <MenuItem
          onClick={() => {
            handleClose();
            handleLogout();
          }}
          sx={{
            color: 'var(--text-primary)',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            margin: '0.25rem',
            backgroundColor: isDarkMode 
              ? 'rgba(255, 255, 255, 0.05) !important' 
              : 'rgba(0, 0, 0, 0.05) !important',
            border: isDarkMode 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: isDarkMode 
                ? 'rgba(255, 255, 255, 0.05) !important' 
                : 'rgba(0, 0, 0, 0.05) !important',
              boxShadow: '0 0 0 1px rgba(108, 48, 130, 0.4)',
              border: '1px solid rgba(108, 48, 130, 0.5)',
            }
          }}
        >
          <LogoutIcon sx={{ mr: 2, fontSize: '20px' }} />
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            Logout
          </Typography>
        </MenuItem>
      </Menu>

      {/* CSS for pulse animation */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
          }
          70% {
            transform: scale(1.1);
            box-shadow: 0 0 0 10px rgba(255, 107, 107, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
          }
        }
      `}</style>
    </>
  );
};

export default ProfileMenu;